import type { MouseEvent, ReactNode } from 'react';
import { useTranslations } from 'use-intl';
import { Badge, Button, I, type ButtonSize } from '@leanin/ui';

export type FilterButtonProps = {
  count?: number;
  label?: ReactNode;
  size?: ButtonSize;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
};

export function FilterButton({
  count = 0,
  label,
  size = 'md',
  onClick,
  disabled,
  ...aria
}: FilterButtonProps) {
  const t = useTranslations('Common');
  return (
    <Button
      variant="secondary"
      size={size}
      icon={I.filter}
      // Filter buttons sit beside a module-surface SearchInput (faint
      // --surface-module hairline + soft shadow). Match that hairline weight
      // so the pair reads consistently; the secondary variant's full-strength
      // --border read heavier ("too thick") next to the search bar. Border
      // only — the pill stays grounded (no shadow), unlike the floating input.
      style={{ border: 'var(--surface-module-border)' }}
      {...(onClick !== undefined ? { onClick } : {})}
      {...(disabled !== undefined ? { disabled } : {})}
      {...aria}
    >
      {label ?? t('widgets.filters.label')}
      {count > 0 ? (
        <Badge
          tone="primary"
          style={{
            // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- Badge primitive does not expose margin props
            marginInlineStart: 'var(--space-sm)',
          }}
        >
          {count}
        </Badge>
      ) : null}
    </Button>
  );
}
